export const SENSOR_TYPE_BY_ID = {
  1: 'GPS_LOCATION_TRACKING',
  2: 'INDOOR_AIR_QUALITY_MONITORING',
  3: 'TEMPERATURE_HUMIDITY_SENSING',
  4: 'GPS_TRACKING_ADVANCED',
  5: 'DISTANCE_LEVEL_SENSING',
  6: 'MOTION_DETECTION',
  8: 'PEOPLE_COUNTING',
  9: 'CURRENT_MONITORING',
  10: 'MAGNETIC_FIELD_SENSING',
  12: 'WATER_LEAK_DETECTION',
  13: 'AIR_QUALITY_MONITORING',
  15: 'MULTI_FUNCTION_TRACKING',
  18: 'SMART_ROOM_SENSING',
  21: 'CURRENT_TRANSFORMER_MONITORING',
  28: 'VOLUME_MEASUREMENT',
  29: 'BUTTON_INPUT_DEVICE',
  30: 'GENERIC_SENSOR_PLATFORM_D23',
  31: 'TEMPERATURE_HUMIDITY_PROBE',
  32: 'DOOR_WINDOW_SENSOR',
  33: 'INDUSTRIAL_MONITORING',
  34: 'SOIL_ENVIRONMENTAL_SENSING',
  35: 'PH_LEVEL_MONITORING',
  36: 'LEAF_MOISTURE_SENSING',
  37: 'GENERIC_SENSOR_PLATFORM_D20',
  38: 'SOIL_TEMPERATURE_MONITORING',
  39: 'THERMAL_MONITORING',
  47: 'DOOR_WINDOW_STATUS',
  50: 'ANALYTICS_PLATFORM',
  51: 'ASSET_TRACKING',
  59: 'TEMPERATURE_HUMIDITY_SENSOR',
  60: 'DISTANCE_DETECTION_SENSOR',
  71: 'DOOR_CONTACT_SENSOR',
  // Dragino LoRaWAN LB-series.
  // AIS01-LB confirmed via GET /api/web/lora-sensors (device_type: 84).
  84: 'AI_METER_READER',        // Dragino AIS01-LB
  // TODO(dragino-sw3l-ps-lb branch): add WATER_FLOW_SENSOR (SW3L-LB) and
  // ANALOG_PROBE_SENSOR (PS-LB) here with their REAL upstream device_type IDs
  // once lora.airagri.tech registers those models. They were intentionally
  // NOT given placeholder IDs (100/101) — guessed IDs risk colliding with an
  // unrelated device type upstream and silently mis-routing live frames to
  // the wrong decoder. The decoders are already registered in LoraProcessor
  // and only fire once a sensor with that part_number is created.
};

// Named sensor-type constant so component code can compare against
// sensor.part_number without hardcoding the string literal (F4). Mirrors the
// backend Sensor::AI_METER_READER constant, same as SENSOR_TYPE_BY_ID does.
export const SENSOR_TYPE_AI_METER_READER = 'AI_METER_READER';

// CPL03 wired as a pulse-output water-meter front-end. Same device + decoder
// as DOOR_CONTACT_SENSOR; the difference is the card display (water icon, m³
// instead of count) and per-sensor litres_per_pulse configuration.
export const SENSOR_TYPE_DOOR_CONTACT_SENSOR_WATER = 'DOOR_CONTACT_SENSOR_WATER';
export const SENSOR_TYPE_DOOR_CONTACT_SENSOR = 'DOOR_CONTACT_SENSOR';

// Some upstream device_types can be configured as more than one AirAgri
// sensor_type (e.g. a CPL03 can be used as a door counter OR a water-meter
// pulse counter). The PartNumberRenderFactory uses this to offer the user a
// choice in the "Add Sensor" type dropdown. Keep keys numeric (device_type
// IDs) and values arrays of part_number string constants.
export const ADDITIONAL_PART_NUMBERS_BY_DEVICE_TYPE = {
  71: [SENSOR_TYPE_DOOR_CONTACT_SENSOR_WATER], // CPL03: door OR water meter
};

export const DEVICE_TYPE_ID_BY_NAME = {
  'browan-industrial': 1,
  'browan-tbdw100': 47,
  'TBHH100': 3,
  'browan-tbhv100': 13,
  'browan-tbms100': 10,
  'browan-tabs': 1,
  'browan-tbwl100': 12,
  'oyster-3': 15,
  'yabby-edge': 1,
  'yabby-mk1': 15,
  'dm-yabby3': 15,
  'dragino-laq4': 13,
  'LDDS20': 5,
  'dragino-lds02': 32,
  'lgt92': 4,
  'dragino-lht52': 31,
  'dragino-lht65n': 3,
  'dragino-llds12': 5,
  'dragino-llms01': 36,
  'dragino-llms': 36,       // live lora.airagri.tech uses this slug too
  'dragino-lse01': 34,
  'dragino-LSN50V2_d20': 37,
  'dragino-LSN50V2_d23': 30,
  'dragino-LSN50v2-S31': 38,
  'dragino-lsph01': 35,
  'dragino-ltc2': 9,
  'esp32-paxcounter': 8,
  'general-tracker': 1,
  'glamos-walker': 1,
  'go-iot-wiska': 33,
  'harmony-analytica-harmony': 50,
  'merryiot-indoor-air-quality': 2,
  'merryiot-motion': 6,
  'milesight-at101': 51,
  'milesight-em300-th': 3,
  'milesight-em310-udl': 5,
  'otto-ct100': 21,
  'quandify-cubicmeter': 28,
  'rak-trackit': 4,
  'rakwireless-WisNode-Button-4K': 29,
  'rejeee-sl100': 3,
  'sensecap-s2101': 3,
  'sensecap-t1000': 4,
  'tektelic-vivid': 18,
  'ttgo-tbeam': 1,
  'vutility-hotdrop': 39,
  'dragino-cpl03': 71,
  'dragino-ais01-lb': 84,
  // 'dragino-sw3l-lb' / 'dragino-ps-lb' intentionally omitted until their real
  // upstream device_type IDs are confirmed (see TODO in SENSOR_TYPE_BY_ID).
};

// Reverse map: numeric device_type id → known brand slug. Used for
// resolving a model/brand label when the upstream payload doesn't carry
// device_brand.name directly.
export const DEVICE_BRAND_BY_TYPE_ID = Object.entries(DEVICE_TYPE_ID_BY_NAME)
  .reduce((acc, [slug, id]) => {
    if (!(id in acc)) acc[id] = slug;
    return acc;
  }, {});

// Model-code display label for a brand slug — the physical model code
// printed on the device label (e.g. "AIS01-LB"), not the marketing name.
// This is what the user expects to see on cards and in dropdowns.
export const BRAND_DISPLAY_LABEL = {
  'dragino-ais01-lb': 'AIS01-LB',
  'dragino-sw3l-lb': 'SW3L-LB',
  'dragino-ps-lb': 'PS-LB',
  'dragino-cpl03': 'CPL03',
  'dragino-lds02': 'LDS02',
  'dragino-lht52': 'LHT52',
  'dragino-lht65n': 'LHT65N',
  'dragino-llds12': 'LLDS12',
  'dragino-llms01': 'LLMS01',
  'dragino-llms': 'LLMS01',
  'dragino-lse01': 'LSE01',
  'dragino-lsph01': 'LSPH01',
  'dragino-ltc2': 'LTC2',
  'dragino-laq4': 'LAQ4',
};

// Reverse map: sensor type constant → physical model code.
// Used by the Sensors page card title as a fallback when the backend
// hasn't surfaced device_brand info but the user has picked a part_number
// in admin. One model per constant for Dragino LB-series sensors.
export const MODEL_CODE_BY_SENSOR_TYPE = {
  AI_METER_READER: 'AIS01-LB',
  WATER_FLOW_SENSOR: 'SW3L-LB',
  ANALOG_PROBE_SENSOR: 'PS-LB',
  DOOR_CONTACT_SENSOR: 'CPL03',
};

// Convert an UPPER_SNAKE_CASE sensor-type constant to a Title Case label,
// e.g. 'AI_METER_READER' → 'AI Meter Reader'.
// Intentional: words of length <= 2 are left as-is (uppercased) so short
// acronyms like "AI" stay "AI" rather than becoming "Ai". Longer all-caps
// words (e.g. "LORA") still get title-cased to "Lora" — acceptable for our
// current constants; revisit with an acronym allow-list if a 3+ char acronym
// (e.g. "GPS") ever needs to stay uppercase.
export const humanizeSensorTypeName = (name) => {
  if (!name || typeof name !== 'string') return '';
  return name
    .split('_')
    .map((w) => (w.length <= 2 ? w : w.charAt(0) + w.slice(1).toLowerCase()))
    .join(' ');
};

// DEV EUI heuristic: 16 hex chars (LoRaWAN), with or without separators.
const DEV_EUI_RE = /^[0-9a-fA-F]{16}$/;
export const looksLikeDevEui = (s) =>
  typeof s === 'string' && DEV_EUI_RE.test(s.replace(/[-:]/g, ''));

// Best-effort friendly label for a lora.airagri.tech device record.
// Prefers the brand display label, then humanised sensor-type constant,
// then the brand slug, then device.name (EUI), then a generic fallback.
// Pass { includeEui: true } to append the EUI in parens when available.
export const getLoraDeviceLabel = (device, opts = {}) => {
  if (!device) return '';

  const brandSlug =
    (device.device_brand && device.device_brand.name) ||
    DEVICE_BRAND_BY_TYPE_ID[device.device_type] ||
    null;

  const sensorTypeConst = SENSOR_TYPE_BY_ID[device.device_type] || null;

  let primary =
    (brandSlug && BRAND_DISPLAY_LABEL[brandSlug]) ||
    (sensorTypeConst && humanizeSensorTypeName(sensorTypeConst)) ||
    brandSlug ||
    null;

  if (!primary) {
    // Last resort: avoid showing the raw EUI as the main label.
    primary = looksLikeDevEui(device.name) ? 'Unknown LoRa Device' : device.name;
  }

  if (opts.includeEui && device.name && looksLikeDevEui(device.name)) {
    return `${primary} (${device.name})`;
  }
  return primary;
};

